/** @jsxImportSource theme-ui @jsxRuntime classic */
import { graphql } from 'gatsby';
import invariant from 'invariant';
import { Helmet } from 'react-helmet';
import React, { Fragment, useEffect, useMemo } from 'react';

import { Layout } from '../../layouts';
import BusinessDetailWideCtaBlock from '../../blocks/BusinessDetailWideCtaBlock';
import BusinessDetailCalloutGridBlock from '../../blocks/BusinessDetailCalloutGridBlock';
import BusinessDetailFeaturedStoriesBlock from '../../blocks/BusinessDetailFeaturedStoriesBlock';
import BusinessDetailHeroBlock from '../../blocks/BusinessDetailHeroBlock';
import BusinessDetailGalleryCarouselBlock from '../../blocks/BusinessDetailGalleryCarouselBlock';
import ArchivedBusinessHeroBlock from '../../blocks/ArchivedBusinessHeroBlock';
import {
  FeaturedRetailers,
  FeaturedRetailersProps,
} from '../../components/FeaturedRetailers';
import { PrivateEventsImageCallout } from '../../blocks/PrivateEvents/PrivateEventsImageCallout';

import type { PageConfigFn } from '../../PageConfig';

export const pageConfig: PageConfigFn = ({ pageContext }) => {
  switch (pageContext?.category__category) {
    case 'amenity': {
      return {
        pageName: 'Amenity',
        theme: 'Rock Center Navy',
      };
    }
    case 'dine': {
      return {
        pageName: 'Dine',
        theme: 'Rock Center Cream',
      };
    }
    case 'shop': {
      return {
        pageName: 'Shop',
        theme: 'Rock Center Green',
      };
    }
    default: {
      return {};
    }
  }
};

const categorySlug = (category: string): string => {
  if (category === 'shop') {
    return 'shops';
  }
  if (category === 'amenity') {
    return 'amenities';
  }
  if (category === 'dine') {
    return 'dine';
  }
  throw new Error(`unknown category ${category}`);
};

const BusinessDetail = ({
  data,
}: {
  data: Queries.BusinessDetailQuery;
}): React.JSX.Element => {
  const { business } = data;
  const structuredJSON = {
    '@context': 'https://schema.org',
    '@type': [
      'LocalBusiness',
      (() => {
        switch (business?.category?.category) {
          case 'amenity':
            return 'Store';
          case 'dine':
            return 'Restaurant';
          case 'shop':
            return 'Store';
          default:
            return undefined;
        }
      })(),
    ],
    'image': [business?.poster?.asset?.gatsbyImageData?.images?.fallback?.src],
    '@id': business?.website?.url,
    'name': business?.titleAndSlug?.title,
    'description': '', // business?._rawBody?[0].children[0].text
    'address': {
      '@type': 'PostalAddress',
      'streetAddress': business?.location?.address1,
      'addressLocality': 'New York',
      'addressRegion': 'NY',
      'postalCode': '10019',
      'addressCountry': 'US',
    },
    'url': business?.website?.url,
    'telephone': '+12122459600',
    'currenciesAccepted': 'USD',
    'priceRange': '$',
    'paymentAccepted': 'CASH, CREDIT CARD',
    'openingHours': (business?.useHolidayHours
      ? business?.holidayHours
      : business?.hours
    )?.map((item) => `${item?.day} ${item?.opensAt}-${item?.closesAt}`),
  };

  const directoryPage = (() => {
    switch (business?.category?.category) {
      case 'amenity':
        return 'amenityDirectoryPage';
      case 'dine':
        return 'dineDirectoryPage';
      case 'shop':
        return 'shopDirectoryPage';
      default:
        return undefined;
    }
  })();

  invariant(
    directoryPage,
    `Unknown business category: ${business?.category?.category}`,
  );

  const retailData =
    directoryPage !== undefined && data[directoryPage]?.featuredRetailers;

  invariant(retailData, 'Featured retailers is required');

  const featuredRetailerData = useMemo(() => {
    if (!retailData) return { title: '', retailers: [] };

    return {
      title: retailData.title ?? '',
      retailers: (retailData.retailers ??
        []) as FeaturedRetailersProps['retailers'],
    };
  }, [retailData]);

  // Removes the canonical link injected by Gatsby if the business is archived
  useEffect(() => {
    if (document && data.business?.archive) {
      const canonicalLinkGatsbyInjected = document?.querySelector(
        `link[rel="canonical"]`,
      );
      if (canonicalLinkGatsbyInjected) {
        canonicalLinkGatsbyInjected.remove();
      }
    }
  }, [data.business?.archive]);

  return (
    <Layout>
      {data.business?.archive === true ? (
        <Fragment>
          <Helmet>
            <link
              href={`https://www.rockefellercenter.com/${categorySlug(
                data?.business?.category?.category ?? '',
              )}/`}
              rel="canonical"
            />
          </Helmet>
          <ArchivedBusinessHeroBlock data={data} />
          <FeaturedRetailers
            retailers={featuredRetailerData.retailers}
            title={featuredRetailerData.title}
          />
        </Fragment>
      ) : (
        <Fragment>
          <BusinessDetailHeroBlock data={data} />
          <BusinessDetailGalleryCarouselBlock data={data} />
          {data.business?.imageCalloutToggle && data.business?.imageCallout && (
            <PrivateEventsImageCallout
              {...(data?.business?.imageCallout as Queries.SanityImageCallout)}
              theme="Rock Center Black"
              title={
                data?.business?.imageCallout?.title?.replace(
                  /{restaurant name}/g,
                  data?.business?.titleAndSlug?.title ?? '',
                ) ?? ''
              }
            />
          )}
          <BusinessDetailWideCtaBlock
            id="business-detail-wide-cta"
            theme="Rock Center"
          />
          <BusinessDetailCalloutGridBlock
            id="business-detail-callout-grid"
            theme="Rock Center"
          />
          <BusinessDetailFeaturedStoriesBlock
            featuredStories={
              (data.business?.featuredStories?.length
                ? data.business?.featuredStories
                : data[directoryPage]?.featuredStories) ?? []
            }
            id="atlas"
            theme="Rock Center Green"
          />
        </Fragment>
      )}
      <Helmet>
        <script type="application/ld+json">
          {JSON.stringify(structuredJSON)}
        </script>
      </Helmet>
    </Layout>
  );
};

export const query = graphql`
  fragment DirectoryPageFeaturedStories on SanityBusinessDirectoryPage {
    featuredStories {
      titleAndSlug {
        slug {
          current
        }
        title
      }
      category
      excerpt
      formattedPublishAt: publishAt(formatString: "MMM D YYYY")
      poster {
        alt
        asset {
          gatsbyImageData(
            layout: CONSTRAINED
            placeholder: BLURRED
            formats: [AUTO, WEBP]
          )
        }
      }
    }
  }

  fragment DirectoryPageFeaturedRetailers on SanityBusinessDirectoryPage {
    featuredRetailers {
      title
      retailers {
        eyebrow
        title
        link {
          caption
          url
        }
        poster {
          alt
          asset {
            gatsbyImageData(
              layout: CONSTRAINED
              placeholder: BLURRED
              formats: [AUTO, WEBP]
            )
          }
        }
      }
    }
  }

  query BusinessDetail($id: String!) {
    meta: sanityBusiness(id: { eq: $id }) {
      seo {
        title: metaTitle
        description: metaDescription
        canonical: metaCanonicalUrl
        ogImage: metaImage {
          asset {
            gatsbyImageData(
              layout: CONSTRAINED
              width: 1200
              height: 630
              formats: [AUTO, WEBP]
              placeholder: BLURRED
            )
          }
        }
      }
    }
    business: sanityBusiness(id: { eq: $id }) {
      id
      _rawBody
      archive
      under30Offer
      titleAndSlug {
        title
        slug {
          current
        }
      }
      poster {
        caption
        alt
        asset {
          gatsbyImageData(
            layout: CONSTRAINED
            placeholder: BLURRED
            formats: [AUTO, WEBP]
          )
        }
      }
      imageGallery {
        images {
          asset {
            gatsbyImageData(
              layout: CONSTRAINED
              placeholder: BLURRED
              formats: [AUTO, WEBP]
            )
          }
          alt
        }
      }
      imageCalloutToggle
      imageCallout {
        title
        description
        image {
          align
          alt
          asset {
            gatsbyImageData(layout: CONSTRAINED)
          }
        }
        link {
          url
          caption
        }
      }
      holidayHours {
        opensAt
        day
        closesAt
      }
      hours {
        opensAt
        day
        closesAt
      }
      useHolidayHours
      location {
        title
        address1
        address2
      }
      contactsInfo {
        type
        value
      }
      orderNow {
        caption
        url
      }
      reservation {
        url
        type
      }
      category {
        category
      }
      website {
        url
        caption
      }
      featuredStories {
        titleAndSlug {
          slug {
            current
          }
          title
        }
        category
        excerpt
        formattedPublishAt: publishAt(formatString: "MMM D YYYY")
        poster {
          alt
          asset {
            gatsbyImageData(
              layout: CONSTRAINED
              placeholder: BLURRED
              formats: [AUTO, WEBP]
            )
          }
        }
      }
    }
    amenityDirectoryPage: sanityBusinessDirectoryPage(
      _id: { eq: "amenityDirectoryPage" }
    ) {
      ...DirectoryPageFeaturedStories
      ...DirectoryPageFeaturedRetailers
    }
    dineDirectoryPage: sanityBusinessDirectoryPage(
      _id: { eq: "dineDirectoryPage" }
    ) {
      ...DirectoryPageFeaturedStories
      ...DirectoryPageFeaturedRetailers
    }
    shopDirectoryPage: sanityBusinessDirectoryPage(
      _id: { eq: "shopDirectoryPage" }
    ) {
      ...DirectoryPageFeaturedStories
      ...DirectoryPageFeaturedRetailers
    }
  }
`;

export default BusinessDetail;
